import React, { useState, useEffect } from 'react';
import { FormControlLabel, Radio, RadioGroup, Button, TextField, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@material-ui/icons/Edit';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

const useStyles = makeStyles({
  noOutline: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
        borderWidth: '1px',
      },
      '&.Mui-error fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
      },
    },
  },
});

const ActivityWaiver = ({ waiverFormData, setWaiverFormData }) => {
  const classes = useStyles();
  const [checkboxInput, setCheckboxInput] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);

  const handleWaiverStatusChange = (e) => {
    const status = e.target.value === 'Active';
    setWaiverFormData({
      waiverStatus: { ...waiverFormData.waiverStatus, value: status },
      messageText: { ...waiverFormData.messageText, value: '', errors: {} },
      checkboxes: { ...waiverFormData.checkboxes, value: [] },
    });
    setCheckboxInput('');
    setEditingIndex(null);
  };

  const handleAddCheckbox = () => {
    if (checkboxInput.trim()) {
      const currentCheckboxes = waiverFormData.checkboxes.value || [];
      setWaiverFormData({
        checkboxes: {
          ...waiverFormData.checkboxes,
          value: [...currentCheckboxes, { label: checkboxInput.trim() }],
          errors: {},
        },
      });
      setCheckboxInput('');
    }
  };

  const handleEditCheckbox = (index) => {
    setEditingIndex(index);
    setCheckboxInput(waiverFormData.checkboxes.value[index].label);
    setWaiverFormData({
      isEditing: { value: true },
    });
  };

  const handleSaveEdit = () => {
    if (editingIndex !== null && checkboxInput.trim()) {
      const updatedCheckboxes = [...waiverFormData.checkboxes.value];
      updatedCheckboxes[editingIndex] = { label: checkboxInput.trim() };
      setWaiverFormData({
        checkboxes: {
          ...waiverFormData.checkboxes,
          value: updatedCheckboxes,
        },
        isEditing: { value: false },
      });
      setEditingIndex(null);
      setCheckboxInput('');
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setCheckboxInput('');
    setWaiverFormData({
      isEditing: { value: false },
    });
  };

  const handleRemoveCheckbox = (index) => {
    const updatedCheckboxes = waiverFormData.checkboxes.value.filter((_, i) => i !== index);
    setWaiverFormData({
      checkboxes: {
        ...waiverFormData.checkboxes,
        value: updatedCheckboxes,
      },
    });
    if (editingIndex === index) {
      setEditingIndex(null);
      setCheckboxInput('');
    }
  };

  const messageTextLength = waiverFormData.messageText.value?.length || 0;
  const checkboxInputLength = checkboxInput.length;

  const onDragEnd = (dragResult) => {
    if (!dragResult.destination) return;

    const reorderedCheckboxes = Array.from(waiverFormData.checkboxes.value);
    const [removed] = reorderedCheckboxes.splice(dragResult.source.index, 1);
    reorderedCheckboxes.splice(dragResult.destination.index, 0, removed);

    setWaiverFormData({
      checkboxes: {
        ...waiverFormData.checkboxes,
        value: reorderedCheckboxes,
      },
    });
  };

  return (
    <div className='row px-3 w-100 d-flex mt-4 form-scroll'>
      <div className='custom-upload col-md-12'>
        <RadioGroup
          row
          aria-label='Waiver Status'
          onChange={handleWaiverStatusChange}
          value={waiverFormData.waiverStatus.value ? 'Active' : 'Inactive'}
          name='waiverStatus'
          className='document-type mb-3'
        >
          <FormControlLabel
            value='Active'
            control={<Radio color='primary' />}
            label='Active'
            className='me-5'
          />
          <FormControlLabel
            value='Inactive'
            control={<Radio color='primary' />}
            label='Inactive'
          />
        </RadioGroup>
      </div>

      {waiverFormData.waiverStatus.value && (
        <>
          <div className='row'>
            <div className='col-md-12 mb-1'>
              <TextField
                multiline
                rows={6}
                variant='outlined'
                fullWidth
                className={classes.noOutline}
                style={{ marginTop: '8px' }}
                placeholder='Message Text *'
                value={waiverFormData.messageText.value}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 500) {
                    setWaiverFormData({
                      messageText: {
                        ...waiverFormData.messageText,
                        value: value,
                        errors: value.trim() ? {} : { required: 'Message Text is required' },
                      },
                    });
                  }
                }}
                error={!!waiverFormData.messageText.errors?.required}
                helperText={waiverFormData.messageText.errors?.required}
                inputProps={{ maxLength: 500 }}
              />
              <div
                style={{
                  textAlign: 'right',
                  fontSize: '12px',
                  color: '#999',
                  marginTop: '4px',
                }}
              >
                {messageTextLength}/500
              </div>
            </div>
          </div>

          <div className='row'>
            <div className='col-md-6 mb-3'>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                <div style={{ flex: 1 }}>
                  <TextField
                    variant='standard'
                    fullWidth
                    label={<span>Checkbox Label <span style={{ color: '#f44336' }}>*</span></span>}
                    value={checkboxInput}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 100) {
                        setCheckboxInput(value);
                      }
                    }}
                    inputProps={{ maxLength: 100 }}
                    InputProps={{
                      endAdornment: (
                        <span style={{ fontSize: '12px', color: '#999', whiteSpace: 'nowrap' }}>
                          {checkboxInputLength}/100
                        </span>
                      ),
                    }}
                  />
                </div>
                <Tooltip title='Label text for the terms & conditions checkbox' placement='right'>
                  <InfoIcon fontSize='small' style={{ color: '#666', marginBottom: '4px' }} />
                </Tooltip>
              </div>
            </div>
          </div>

          <div className='row'>
            <div className='col-md-12 mb-3'>
              {editingIndex !== null ? (
                <>
                  <Button
                    variant='contained'
                    color='primary'
                    onClick={handleSaveEdit}
                    disabled={!checkboxInput.trim()}
                    style={{ textTransform: 'none', marginRight: '8px' }}
                  >
                    Save
                  </Button>
                  <Button
                    variant='outlined'
                    onClick={handleCancelEdit}
                    style={{ textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  variant='outlined'
                  color='primary'
                  onClick={handleAddCheckbox}
                  disabled={!checkboxInput.trim()}
                  style={{ textTransform: 'none' }}
                >
                  Add
                </Button>
              )}
            </div>
          </div>

          {waiverFormData.checkboxes.value && waiverFormData.checkboxes.value.length > 0 && (
            <div className='mt-2'>
              <div className='col-md-11 mt-3 ps-2 item-list-container'>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId='droppableCheckboxes'>
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        className='col-md-11'
                        ref={provided.innerRef}
                      >
                        {waiverFormData.checkboxes.value.map((checkbox, index) => (
                          <Draggable key={index} draggableId={'dragId' + index} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className='col row custom-duration-box'
                              >
                                <div className='row mx-2 align-items-center'>
                                  <IconButton
                                    aria-label='drag'
                                    disabled={true}
                                    className='col-1'
                                  >
                                    <DragIndicatorIcon fontSize='large' />
                                  </IconButton>
                                  <div className='col-md-5 text w-75'>
                                    <span
                                      style={{
                                        flex: 1,
                                        wordBreak: 'break-word',
                                        overflow: 'hidden',
                                        display: 'block',
                                      }}
                                    >
                                      {checkbox.label}
                                    </span>
                                  </div>
                                  <div className='col-md-1'>
                                    <IconButton onClick={() => handleEditCheckbox(index)}>
                                      <EditIcon fontSize='small' className='icon' />
                                    </IconButton>
                                  </div>
                                  <div className='col-md-1'>
                                    <IconButton onClick={() => handleRemoveCheckbox(index)}>
                                      <RemoveCircleIcon fontSize='small' className='icon' />
                                    </IconButton>
                                  </div>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ActivityWaiver;
